import type { TFunction } from 'i18next';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { GoogleSignInButton } from '../../components/GoogleSignInButton';
import { PasswordInput } from '../../components/PasswordInput';
import { useFeatures } from '../../hooks/useFeatures';
import { APIError, idpApi } from '../../services/idp-api';

function getSignInErrorMessage(error: unknown, t: TFunction<'idp'>): string {
  if (!(error instanceof APIError)) {
    return t('signIn.errors.default');
  }

  if (error.code === 'INVALID_CREDENTIALS') {
    return t('signIn.errors.invalidCredentials');
  }

  if (error.code === 'ACCOUNT_LOCKED') {
    return t('signIn.errors.accountLocked');
  }

  if (error.code === 'CAPTCHA_REQUIRED') {
    return t('signIn.errors.captchaRequired');
  }

  if (error.code === 'RATE_LIMITED') {
    return t('signIn.errors.rateLimited');
  }

  if (error.code === 'EMAIL_EXISTS_DIFFERENT_CREDENTIAL') {
    return t('signIn.errors.emailExistsDifferentCredential');
  }

  return error.message;
}

function hasNoGoogleCallbackParams(code: string, googleError: string): boolean {
  return code === '' && googleError === '';
}

function hasGoogleError(googleError: string): boolean {
  return googleError !== '';
}

function hasInvalidGoogleCallback(code: string, flowId: string): boolean {
  return code === '' || flowId === '';
}

function hasInteractionFlow(interactionId: string): boolean {
  return interactionId !== '';
}

export function SignInPage() {
  const { t } = useTranslation('idp');
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { google: googleEnabled } = useFeatures();
  const interactionId = searchParams.get('interaction')?.trim() ?? '';
  const isVerified = searchParams.get('verified') === 'true';
  const isPasswordReset = searchParams.get('reset') === 'success';

  useEffect(() => {
    const code = searchParams.get('code')?.trim() ?? '';
    const flowId = searchParams.get('flowId')?.trim() ?? '';
    const googleError = searchParams.get('error')?.trim() ?? '';

    if (hasNoGoogleCallbackParams(code, googleError)) {
      return;
    }

    if (hasGoogleError(googleError)) {
      setErrorMessage(t('signIn.googleCancelledError'));
      return;
    }

    if (hasInvalidGoogleCallback(code, flowId)) {
      setErrorMessage(t('signIn.googleExpiredError'));
      return;
    }

    setIsGoogleSubmitting(true);
    setErrorMessage(null);

    void (async () => {
      try {
        const response = await idpApi.googleCallback({ code, flowId });
        setIsRedirecting(true);
        window.location.assign(response.redirectUri);
      } catch (error) {
        setErrorMessage(getSignInErrorMessage(error, t));
        setIsGoogleSubmitting(false);
      }
    })();
  }, [searchParams, t]);

  const isFormInvalid = useMemo(
    () => email.trim().length === 0 || password.length === 0,
    [email, password]
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const request: { email: string; password: string; flowId?: string } = {
        email: email.trim(),
        password,
      };
      if (hasInteractionFlow(interactionId)) {
        request.flowId = interactionId;
      }
      const response = await idpApi.authenticate(request);

      setIsRedirecting(true);
      window.location.assign(response.redirectUri ?? '/account');
    } catch (error) {
      setErrorMessage(getSignInErrorMessage(error, t));
      setIsSubmitting(false);
    }
  }

  if (isRedirecting) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
        <Card sx={{ width: '100%', maxWidth: 520 }}>
          <CardContent>
            <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">
                {t('signIn.redirecting')}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 520 }}>
        <CardContent>
          <Stack
            spacing={3}
            component="form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
          >
            <Stack spacing={1}>
              <Typography variant="h5" component="h1" fontWeight={700}>
                {t('signIn.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('signIn.subtitle')}
              </Typography>
            </Stack>

            {isVerified ? (
              <Alert severity="success">{t('signIn.emailVerifiedSuccess')}</Alert>
            ) : null}
            {isPasswordReset ? (
              <Alert severity="success">{t('signIn.passwordResetSuccess')}</Alert>
            ) : null}
            {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

            {googleEnabled ? (
              <>
                <GoogleSignInButton flowId={interactionId} label={t('signIn.continueWithGoogle')} />
                <Divider>
                  <Typography variant="body2" color="text.secondary">
                    {t('signIn.or')}
                  </Typography>
                </Divider>
              </>
            ) : null}

            <TextField
              label={t('signIn.emailLabel')}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              required
              fullWidth
            />

            <PasswordInput
              label={t('signIn.passwordLabel')}
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              showStrength={false}
              required
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || isGoogleSubmitting || isFormInvalid}
            >
              {isSubmitting ? t('signIn.submittingButton') : t('signIn.submitButton')}
            </Button>

            <Link
              component={RouterLink}
              to="/auth/forgot-password"
              underline="hover"
              variant="body2"
            >
              {t('signIn.forgotPassword')}
            </Link>

            <Divider />

            <Stack spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t('signIn.noAccount')}
              </Typography>
              <Button
                component={RouterLink}
                to={
                  hasInteractionFlow(interactionId)
                    ? `/auth/signup?interaction=${encodeURIComponent(interactionId)}`
                    : '/auth/signup'
                }
                variant="outlined"
                fullWidth
              >
                {t('signIn.signUpButton')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
